/**
 * AURA Orientation Adjustments Module
 * Groups controls into logical sections (Media & Motion, Reading Aids,
 * Focus & Cursor) so every grid row fills completely with no orphan buttons.
 * Includes a "Useful Links" dropdown for quick page navigation.
 */

import { OrientationSettings } from '../../core/types';
import { stateManager } from '../../core/state';
import { eventBus } from '../../core/events';
import { Logger } from '../../core/logger';
import { createIconButton } from '../../ui/controls/icon-button';
import { getIcon } from '../../ui/styles/icons';
import { applyOrientationStyles } from './effects';
import { createUsefulLinks } from './useful-links';

const logger = new Logger('OrientationModule');

interface ButtonRef {
  key: keyof OrientationSettings;
  setChecked: (v: boolean) => void;
}

interface ButtonDef {
  id: string;
  label: string;
  icon: string;
  checked: boolean;
  settingKey: keyof OrientationSettings;
  onChange: (checked: boolean) => void;
}

interface SectionDef {
  title: string;
  columns: 2 | 3;
  buttons: ButtonDef[];
}

/**
 * Initialize the orientation adjustments module
 */
export function initOrientationModule(container: HTMLElement): void {
  logger.info('Initializing orientation module');

  const orientationContainer = container.querySelector('#aura-orientation-adjustments');
  if (!orientationContainer) {
    logger.warn('Orientation adjustments container not found');
    return;
  }

  const settings = stateManager.getSettings().orientation;
  const buttonRefs: ButtonRef[] = [];

  const wrapper = document.createElement('div');
  const sections = buildSectionDefinitions(settings);

  for (const section of sections) {
    const heading = document.createElement('h4');
    heading.className = 'aura-panel__subsection-title';
    heading.textContent = section.title;
    wrapper.appendChild(heading);

    const colsClass = section.columns === 2 ? ' aura-icon-btn-grid--cols-2' : '';
    const grid = document.createElement('div');
    grid.className = `aura-icon-btn-grid${colsClass}`;

    for (const def of section.buttons) {
      const btn = createIconButton({
        id: def.id,
        label: def.label,
        icon: getIcon(def.icon as any),
        checked: def.checked,
        onChange: def.onChange,
      });
      grid.appendChild(btn.element);
      buttonRefs.push({ key: def.settingKey, setChecked: btn.setChecked });
    }

    wrapper.appendChild(grid);
  }

  const usefulLinksEl = createUsefulLinks();
  if (usefulLinksEl) {
    wrapper.appendChild(usefulLinksEl);
  }

  orientationContainer.innerHTML = '';
  orientationContainer.appendChild(wrapper);

  eventBus.on('settings:change', () => {
    const current = stateManager.getSettings().orientation;
    for (const ref of buttonRefs) {
      ref.setChecked(!!current[ref.key]);
    }
    applyOrientationStyles(current);
  });

  applyOrientationStyles(settings);
  logger.info('Orientation module initialized');
}

function updateSetting<K extends keyof OrientationSettings>(
  key: K,
  value: OrientationSettings[K]
): void {
  stateManager.updateSettings({
    orientation: { [key]: value } as Partial<OrientationSettings>,
  });
}

/**
 * Buttons grouped by function so every row fills completely:
 * - Media & Motion  → 3 buttons × 3 cols = 1 full row
 * - Reading Aids    → 3 buttons × 3 cols = 1 full row
 * - Focus & Cursor  → 4 buttons × 2 cols = 2 full rows
 */
function buildSectionDefinitions(s: OrientationSettings): SectionDef[] {
  return [
    {
      title: 'Media & Motion',
      columns: 3,
      buttons: [
        {
          id: 'aura-mute-sounds',
          label: 'Mute Sounds',
          icon: 'muteSounds',
          checked: s.muteSounds,
          settingKey: 'muteSounds',
          onChange: (checked) => updateSetting('muteSounds', checked),
        },
        {
          id: 'aura-hide-images',
          label: 'Hide Images',
          icon: 'hideImages',
          checked: s.hideImages,
          settingKey: 'hideImages',
          onChange: (checked) => updateSetting('hideImages', checked),
        },
        {
          id: 'aura-stop-animations',
          label: 'Stop Animations',
          icon: 'stopAnimations',
          checked: s.stopAnimations,
          settingKey: 'stopAnimations',
          onChange: (checked) => updateSetting('stopAnimations', checked),
        },
      ],
    },
    {
      title: 'Reading Aids',
      columns: 3,
      buttons: [
        {
          id: 'aura-read-mode',
          label: 'Read Mode',
          icon: 'readMode',
          checked: s.readMode,
          settingKey: 'readMode',
          onChange: (checked) => updateSetting('readMode', checked),
        },
        {
          id: 'aura-reading-guide',
          label: 'Reading Guide',
          icon: 'readingGuide',
          checked: s.readingGuide,
          settingKey: 'readingGuide',
          onChange: (checked) => updateSetting('readingGuide', checked),
        },
        {
          id: 'aura-reading-mask',
          label: 'Reading Mask',
          icon: 'readingMask',
          checked: s.readingMask,
          settingKey: 'readingMask',
          onChange: (checked) => updateSetting('readingMask', checked),
        },
      ],
    },
    {
      title: 'Focus & Cursor',
      columns: 2,
      buttons: [
        {
          id: 'aura-highlight-hover',
          label: 'Highlight Hover',
          icon: 'highlightHover',
          checked: s.highlightHover,
          settingKey: 'highlightHover',
          onChange: (checked) => updateSetting('highlightHover', checked),
        },
        {
          id: 'aura-highlight-focus',
          label: 'Highlight Focus',
          icon: 'highlightFocus',
          checked: s.highlightFocus,
          settingKey: 'highlightFocus',
          onChange: (checked) => updateSetting('highlightFocus', checked),
        },
        {
          id: 'aura-big-black-cursor',
          label: 'Big Black Cursor',
          icon: 'bigBlackCursor',
          checked: s.bigBlackCursor,
          settingKey: 'bigBlackCursor',
          onChange: (checked) => {
            if (checked && stateManager.getSettings().orientation.bigWhiteCursor) {
              updateSetting('bigWhiteCursor', false);
            }
            updateSetting('bigBlackCursor', checked);
          },
        },
        {
          id: 'aura-big-white-cursor',
          label: 'Big White Cursor',
          icon: 'bigWhiteCursor',
          checked: s.bigWhiteCursor,
          settingKey: 'bigWhiteCursor',
          onChange: (checked) => {
            if (checked && stateManager.getSettings().orientation.bigBlackCursor) {
              updateSetting('bigBlackCursor', false);
            }
            updateSetting('bigWhiteCursor', checked);
          },
        },
      ],
    },
  ];
}
